import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.scss';
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";

type PropsDef = {
  isOpen: boolean;
  studentName: string;
  onConfirm: () => void;
  onCancel: () => void;
  onCancelWithoutRefund?: () => void;
};

type CancelOption = 'refund' | 'no_refund';

export default function CancelRegistrationModal(props: PropsDef) {
  const { isOpen, studentName, onConfirm, onCancel, onCancelWithoutRefund } = props;
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState<CancelOption>('refund');

  const hasExtendedOptions = !!onCancelWithoutRefund;

  const handleConfirm = () => {
    switch (selectedOption) {
      case 'refund':
        onConfirm();
        break;
      case 'no_refund':
        onCancelWithoutRefund?.();
        break;
    }
  };

  const getConfirmLabel = () => {
    switch (selectedOption) {
      case 'refund':
        return t('Cancel with Refund');
      case 'no_refund':
        return t('Cancel without Refund');
    }
  };

  // Simple 2-button modal for student/scheduler view (backward compatible)
  if (!hasExtendedOptions) {
    return (
      <ModalLayout isOpen={isOpen} classNames={`${styles.modal}`}>
        <div className={styles.confirmation_modal_content}>
          <div className={styles.close_button} onClick={onCancel}>
            <i className="ico ico-x" />
          </div>
          <div className={styles.title_and_content}>
            <div className={styles.title}>
              <i className={`ico ico-calendar-x ${styles.danger}`} />
              <h2>{t("Are you sure you want to cancel {{studentName}}'s registration?", { studentName })}</h2>
            </div>
            <div className={styles.actions}>
              <button
                onClick={onConfirm}
                className={`${styles.confirm_button} ${styles.btn_danger}`}
                data-testid="cancel-registration-confirm"
              >
                {t("Yes, cancel it")}
              </button>
              <button onClick={onCancel} className={`${styles.confirm_button} ${styles.btn_secondary}`}>
                {t("No, keep it")}
              </button>
            </div>
          </div>
        </div>
      </ModalLayout>
    );
  }

  // Extended modal with radio options for admin/coach view
  return (
    <ModalLayout isOpen={isOpen} classNames={`${styles.modal}`}>
      <div className={styles.confirmation_modal_content}>
        <div className={styles.close_button} onClick={onCancel}>
          <i className="ico ico-x" />
        </div>
        <div className={styles.title_and_content}>
          <div className={styles.title}>
            <i className={`ico ico-calendar-x ${styles.danger}`} />
            <h2>{t("What would you like to do with {{studentName}}'s registration?", { studentName })}</h2>
          </div>
          <div className={styles.radio_group}>
            <label className={styles.radio_option}>
              <input
                type="radio"
                name="cancel_option"
                checked={selectedOption === 'refund'}
                onChange={() => setSelectedOption('refund')}
                data-testid="cancel-registration-with-refund"
              />
              <div className={styles.radio_label}>
                <span>{t('Cancel with Refund')}</span>
                <span className={styles.subtitle}>{t('Registration will be cancelled and payment refunded')}</span>
              </div>
            </label>
            <label className={styles.radio_option}>
              <input
                type="radio"
                name="cancel_option"
                checked={selectedOption === 'no_refund'}
                onChange={() => setSelectedOption('no_refund')}
                data-testid="cancel-registration-without-refund"
              />
              <div className={styles.radio_label}>
                <span>{t('Cancel without Refund')}</span>
                <span className={styles.subtitle}>{t('Registration will be cancelled but no refund issued')}</span>
              </div>
            </label>
          </div>
          <div className={styles.actions}>
            <button
              onClick={handleConfirm}
              className={`${styles.confirm_button} ${styles.btn_danger}`}
              data-testid="cancel-registration-submit"
            >
              {getConfirmLabel()}
            </button>
            <button onClick={onCancel} className={`${styles.confirm_button} ${styles.btn_secondary}`}>
              {t("No, keep it")}
            </button>
          </div>
        </div>
      </div>
    </ModalLayout>
  );
}
